import React, { useEffect, useState } from 'react'

import mixpanel from 'mixpanel-browser'
import styled from 'styled-components'

import { fetchImage, fetchPointImage } from '../../../../api/buildingFetches'
import { useBuildingContext } from '../../../../contexts/buildingContext'
import { useNavigation } from '../../../../hooks/useNavigation'
import {
  buildBuildingImageData,
  createObjectURLSafe,
} from '../../../../utils/imageUtils'
import { useImageViewerContext } from '../imageViewerContext'

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const _ScrollContainer = styled.div`
  position: relative;
  width: 100%;
  background: white;
  border-radius: 2px;
  padding: 1em 2em;
  z-index: 100;
  height: 80px;
`

const TimeScrollMarker = (props: {
  date: Date
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image type
  image?: any
  neighboring?: boolean
}) => {
  const getDateString = (d: Date) => {
    if (d.getDate() === 1) {
      const _day = d.getDate()
      const month = d.getMonth()
      const year = d.getFullYear()
      return (
        <span className="rc-slider-month-label">{`${monthNames[month]} ${year}`}</span>
      )
    } else if (d.getDate() < 3 || props.neighboring) {
      return <></>
    }
    return d.getDate()
  }
  return <>{getDateString(props.date)}</>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper point type
export const TimeScroll = (props: { point: any }) => {
  const { state: buildingState, updateImage } = useBuildingContext()
  const {
    state: ImageViewerState,
    updatePane2,
    updateMaster,
  } = useImageViewerContext()
  const { navigateToPointFullscreen } = useNavigation()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper sliderMarks type
  const [sliderMarks, setSliderMarks] = useState<any>()
  const [defaultPosition, setDefaultPosition] = useState<number>(0)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image type
  const getSetCurrentImageData = async (image: any) => {
    if (
      ImageViewerState[ImageViewerState.focused].data.image_id ===
      image.image_id
    )
      return
    const oldAngle = ImageViewerState[ImageViewerState.focused].data.angle
    const newAngle = image.angle
    const viewpointsImage = await fetchPointImage(
      buildingState.projectId,
      buildingState.floorData.floor_code,
      image.id,
    )
    if (!viewpointsImage) return
    const imageBlob = await fetchImage(viewpointsImage.processed_image_url)
    const imageUrl = createObjectURLSafe(imageBlob)
    if (!imageUrl) return
    const currentImageData = {
      image: imageUrl,
      data: image,
      angleDelta: newAngle - oldAngle,
      observationDate: image.taken_on,
      isMostRecent: true,
    }
    navigateToPointFullscreen(image.point_id, { date: image.taken_on })

    // Update the currently focused pane (master or subordinate) with the new image data
    if (ImageViewerState.focused === 'master') {
      updateMaster(currentImageData)
    } else {
      updatePane2(currentImageData)
    }

    // Keep building image in sync for TagManager hydrate + SafetyTag create.
    if (ImageViewerState.focused === 'master') {
      const previousBuildingUrl = buildingState.imageData?.image as
        | string
        | undefined
      const buildingImageData = await buildBuildingImageData({
        selectedImage: image,
        viewpointsImage,
        imageUrl,
      }).catch(() => null)
      if (buildingImageData) {
        updateImage(buildingImageData)
      }
      if (
        previousBuildingUrl &&
        previousBuildingUrl !== imageUrl &&
        previousBuildingUrl.startsWith('blob:')
      ) {
        URL.revokeObjectURL(previousBuildingUrl)
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper images type
  const sortImages = (images: any) => {
    return images.sort(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image types
      (a: any, b: any) =>
        new Date(b.taken_on).getTime() - new Date(a.taken_on).getTime(),
    )
  }

  const getStartOfMonths = (startDate: Date): Date[] => {
    const diff = new Date().getMonth() - startDate.getMonth()
    const result = []
    if (diff > 0) {
      for (let i = 0; i < diff + 1; i++) {
        const newD = new Date(startDate)
        newD.setMonth(startDate.getMonth() + i)
        result.push(newD)
      }
    }
    return result
  }

  const generateMarks = () => {
    const sorted = sortImages(props.point.images)
    const startDate = new Date(sorted[sorted.length - 1].taken_on)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper marks type
    const marks: any = {
      0: <TimeScrollMarker date={startDate} />,
      100: <TimeScrollMarker date={new Date()} />,
    }

    const difference = new Date().getTime() - new Date(startDate).getTime()

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper date type
    getStartOfMonths(new Date(startDate)).forEach((date: any) => {
      const startDiff = date.getTime() - new Date(startDate).getTime()
      const percentOff = Math.round((startDiff / difference) * 100)
      marks[percentOff] = <TimeScrollMarker date={date} />
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image and index types
    sorted.forEach((image: any, index: any) => {
      if (index === sorted.length) return
      const startDiff =
        new Date(image.taken_on).getTime() - new Date(startDate).getTime()
      const percentOff = Math.round((startDiff / difference) * 100)
      marks[percentOff] = (
        <TimeScrollMarker date={new Date(image.taken_on)} image={image} />
      )
      if (marks[percentOff + 1] && !marks[percentOff + 1].props.neighboring) {
        marks[percentOff + 1] = React.createElement('TimeScrollMarker', {
          ...marks[percentOff + 1].props,
          neighboring: true,
        })
      }
    })

    // fakeDates.forEach((item: any, index: any) => {
    //   if (index === fakeDates.length) return;
    //   let startDiff = new Date(item.date).getTime() - new Date('May 01, 2021').getTime();
    //   let percentOff = Math.round((startDiff / difference) * 100);
    //   marks[percentOff] = (
    //     <TimeScrollMarker date={new Date(item.date)} image={sorted[sorted.length - 1]} />
    //   );
    //   if (marks[percentOff + 1] && !marks[percentOff + 1].props.neighboring) {
    //     marks[percentOff + 1] = React.createElement('TimeScrollMarker', {
    //       ...marks[percentOff + 1].props,
    //       neighboring: true,
    //     });
    //   }
    // });

    setSliderMarks(marks)
  }

  const _onChange = (value: number) => {
    if (sliderMarks[value].props.image) {
      setDefaultPosition(value)
      mixpanel.track('Use Time Scroll')
    }
  }

  const onAfterChange = (value: number) => {
    if (sliderMarks) {
      if (sliderMarks[value].props.image) {
        getSetCurrentImageData(sliderMarks[value].props.image)
      }
    }
  }

  useEffect(() => {
    if (props.point.images) generateMarks()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: generateMarks intentionally omitted to prevent infinite loops
  }, [props.point])

  useEffect(() => {
    onAfterChange(defaultPosition)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: onAfterChange intentionally omitted - parent should wrap in useCallback if needed
  }, [defaultPosition])

  useEffect(() => {
    if (sliderMarks) {
      const currentKey: string | undefined = Object.keys(sliderMarks).find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper key type
        (key: any) => {
          return (
            sliderMarks[key].props.image?._id ===
            ImageViewerState[ImageViewerState.focused].data._id
          )
        },
      )
      if (currentKey) setDefaultPosition(parseInt(currentKey))
    }
  }, [sliderMarks, ImageViewerState, ImageViewerState.focused])

  // Slider UI is not rendered; marks and sync logic still run for any parent that may use them.
  return <></>
}
